import * as React from "react"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import styled from "styled-components"
import { COMPONENT_HEIGHT } from "src/frontend/components/OS/Applications/Reports/Report/Viewer/Layout/LayoutViewer/shared"
import { StackableBarChartLegendType } from "src/frontend/components/OS/Shared/Charts/StackableBarChart/StackableBarChartContext"
import type { StackableBarData } from "src/frontend/components/OS/Shared/Charts/StackableBarChart/stackableShared"
import { DimensionWidgetEmptyView } from "src/frontend/components/OS/Shared/Widgets/DimensionWidget"
import { StackedBarChartComponent } from "src/frontend/components/Shared/Layout/Components/Charts/StackedBarChartComponent"
import { type TimeseriesValue } from "src/frontend/components/Shared/Layout/Components/Charts/toTimeseries"
import {
  type StatementCategoryDetails,
  type SummaryLineRowDetails,
} from "src/frontend/components/Shared/Layout/Components/Statements/toDetailsData"
import { ComponentSize } from "src/frontend/components/Shared/Layout/ComponentSize"
import { useClassBasedChartData } from "src/frontend/components/Shared/Layout/PopOver/useClassBasedChartData"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

/*
  STYLES
*/
export const ChartContainer = styled.div`
  height: 260px;
  padding: 5px 12px;
  position: relative;
`

/*
  INTERFACES
*/
interface ChartProps {
  details: StatementCategoryDetails | SummaryLineRowDetails
}

/*
  COMPONENTS
*/
export const ClassBasedOverTimeChart: React.FC<ChartProps> = ({ details }) => {
  const [selectedIndex, setSelectedIndex] = React.useState<number | undefined>(undefined)
  const { periodName, amountOverTime, data, intervalOrigin } = useClassBasedChartData(details)

  const onMouseOver = React.useCallback(
    (_: TimeseriesValue | StackableBarData | undefined, index: number) => {
      setSelectedIndex(index)
    },
    []
  )

  const onMouseOut = React.useCallback(() => setSelectedIndex(undefined), [])

  return (
    <ChartContainer>
      <StackedBarChartComponent
        title={periodName}
        icon={null}
        componentSize={ComponentSize.Small}
        height={COMPONENT_HEIGHT}
        combinedSeries={amountOverTime}
        data={data}
        loading={false}
        intervalOrigin={intervalOrigin}
        legend={StackableBarChartLegendType.Mini}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
        selectedIndex={selectedIndex}
        emptyNode={<EmptyChartView />}
      />
    </ChartContainer>
  )
}

const EmptyChartView: React.FC = () => {
  const intervalOrigin = useIntervalOrigin()

  return (
    <DimensionWidgetEmptyView>
      No Data for past {dateTimeHelper.displayNameForInterval(intervalOrigin.interval, 12)}
    </DimensionWidgetEmptyView>
  )
}
